import { environmentVars } from 'constants/services';
import { v4 } from 'uuid';
import {
  initialize as initializeLaunchDarkly,
  LDClientBase,
  LDContext,
  LDOptions,
  basicLogger,
} from 'launchdarkly-js-client-sdk';
import { FeatureFlag } from '../constants';

const FEATURE_FLAG_DEFAULTS: Partial<Bluebeam.FeatureFlags.Models.Context> = {
  [FeatureFlag.AddRfiToDrawing]: true,
  [FeatureFlag.DmsRegions]: [],
} as const;

export class LaunchDarklyFeatureFlagService
  implements Bluebeam.Services.IFeatureFlagService
{
  private readonly _client: LDClientBase;

  private readonly _subscriptions = new Map<
    Bluebeam.FeatureFlags.Models.Subscription['id'],
    Bluebeam.FeatureFlags.Models.Subscription['listener']
  >();

  constructor() {
    // Before the user has logged in, we set them as unknown.  The context will be updated via the login machine:
    const defaultContext: LDContext = {
      anonymous: true,
      kind: 'user',
      key: 'UNKNOWN_USER',
    };

    const defaultOptions: LDOptions = {
      privateAttributes: ['email'],
      logger: basicLogger({
        level: 'error', // If you need to debug launch darkly, change the log level here
      }),
    };

    this._client = initializeLaunchDarkly(
      environmentVars.LAUNCHDARKLY_CLIENT_SIDE_ID,
      defaultContext,
      defaultOptions
    );

    const invokeSubscriptions = () => {
      this._subscriptions.forEach((subscription) => {
        subscription();
      });
    };
    this._client.on('change', invokeSubscriptions);
    this._client.on('initialized', invokeSubscriptions);
  }

  getFeature: Bluebeam.Services.IFeatureFlagService['getFeature'] = (key) => {
    return this._client.variation(key, FEATURE_FLAG_DEFAULTS[key] ?? false);
  };

  hasFeature: Bluebeam.Services.IFeatureFlagService['hasFeature'] = (key) => {
    return this._client.variation(key, FEATURE_FLAG_DEFAULTS[key] ?? false);
  };

  setAttributes: Bluebeam.Services.IFeatureFlagService['setAttributes'] =
    async (attributes) => {
      const launchDarklyUser = this._client.getContext();

      await this._client.identify({
        ...launchDarklyUser,
        ...attributes,
      });
    };

  setContext: Bluebeam.Services.IFeatureFlagService['setContext'] = async (
    context: LDContext
  ) => {
    await this._client.identify(context);
  };

  subscribe: Bluebeam.Services.IFeatureFlagService['subscribe'] = (
    listener
  ) => {
    const subscriptionId = v4();

    this._subscriptions.set(subscriptionId, listener);

    return subscriptionId;
  };

  unsubscribe: Bluebeam.Services.IFeatureFlagService['unsubscribe'] = (
    subscriptionId
  ) => {
    this._subscriptions.delete(subscriptionId);
  };
}
